import { LibraryCache } from 'base/library_cache';
import { extractTreeByName, getNodeName } from 'base/webflow/data_attributes';
import {
  isReplaceableTextNode,
  textContentsOf,
  TextReplacementType,
} from 'base/webflow/replaceable_content';
import { Component, NestedNode } from 'base/webflow/schema';
import {
  ElementInstance,
  ElementSourceState,
  Project,
  SlotOverride,
} from 'blocks/editor/model';
import { unrefSection } from 'blocks/editor/model_helpers';
import { SelectionState } from 'blocks/editor/selection/states';
import { computedFn } from 'mobx-utils';
import { getSlotInfoAtNode, isElementSelectable, SlotInfo } from './slots';

export type Interactable = {
  // Where the interaction box should be rendered:
  element: ElementInstance;
  // Undefined means the root node of the element
  nodeId: string | undefined;
  elementRootNodeId: string;
  // The actions available at this box:
  slotItem?: SlotItemInteractionAction;
  text?: TextEditAction;
  children: Interactable[];
};

export type SlotInteractionAction = {
  slotInfo: SlotInfo;
  slotOverride: SlotOverride;
};

export type SlotItemInteractionAction = {
  slotInfo: SlotInfo;
  slotOverride: SlotOverride;
  parentElement: ElementInstance;
  itemElement: ElementInstance;
  dragHandlePosition: 'left' | 'top';
};

export type TextEditAction = {
  element: ElementInstance;
  nodeId: string;
  replacementType: TextReplacementType;
  originalText: string;
};

export function elementAndNodeId(v: Pick<Interactable, 'element' | 'nodeId'>) {
  return `${v.element.oid}/${v.nodeId}`;
}

export const flattenInteractables = computedFn(
  (interactables: readonly Interactable[]): readonly Interactable[] => {
    let out: Interactable[] = [];
    function walk(at: Interactable) {
      out.push(at);
      for (const child of at.children) walk(child);
    }
    for (const interactable of interactables) {
      walk(interactable);
    }
    return out;
  },
);

/** If this selection would offer a text editing interaction */
export function isTextEditingSelection(
  sel: SelectionState & { element: ElementInstance },
  project: Project,
  library: LibraryCache,
) {
  const section = unrefSection(sel.section, project);
  if (!section?.element.current) return false;
  const interactable = flattenInteractables(
    getInteractablesTree(section.element.current, library),
  ).find((i) => i.nodeId === sel.nodeId && i.element === sel.element);
  return interactable?.text != null;
}

/**
 * Returns all nodes which are interactable (e.g. editable) in some way.
 *
 * They are ordered as a pre-order traversal of the node/element tree.
 **/
export const getInteractablesTree = computedFn<
  (el: ElementInstance, library: LibraryCache) => readonly Interactable[]
>((el, library) => {
  const out: Interactable[] = [];

  const getComponent = (slug: string) =>
    library.getCachedPublicComponentJson(slug, { fetchIfMissing: true })?.value;
  const component = getComponent(el.source.slug);
  if (!component) return [];

  const root = extractTreeByName(component, el.source.rootName);

  function visitNode(node: NestedNode) {
    if (node.text) return;

    const slotInfo = getSlotInfoAtNode(node, el.source, component!);
    const slotOverride = slotInfo && el.slotOverrides.get(slotInfo.name);
    const replacementType = isReplaceableTextNode(node);
    if (slotInfo && slotOverride) {
      const items = slotOverride.items || [];
      const slotType = library.getCachedSlotType(slotInfo.typeId, {
        fetchIfMissing: true,
      })?.value;
      const dragHandlePosition = slotType?.dragHandlePosition || 'left';

      for (const childEl of items) {
        const childComponent = getComponent(childEl.source.slug);
        if (!childComponent) continue;

        const slotItemInteractable: Interactable = {
          element: childEl,
          nodeId: undefined,
          elementRootNodeId: getRootNodeId(childComponent, childEl.source),
          slotItem: isElementSelectable({
            element: childEl,
            parentSlotType: slotType,
            library,
          })
            ? {
                slotInfo,
                slotOverride,
                parentElement: el,
                itemElement: childEl,
                dragHandlePosition,
              }
            : undefined,
          children: [],
        };
        out.push(slotItemInteractable);
        const insideItem = getInteractablesTree(childEl, library);
        if (
          insideItem.length === 1 &&
          insideItem[0].text &&
          !insideItem[0].slotItem
        ) {
          slotItemInteractable.text = insideItem[0].text;
        } else {
          slotItemInteractable.children.push(...insideItem);
        }
      }
    } else if (replacementType) {
      out.push({
        element: el,
        nodeId: node._id,
        elementRootNodeId: root._id,
        text: {
          element: el,
          nodeId: node._id,
          replacementType,
          originalText: textContentsOf(node),
        },
        children: [],
      });
    } else {
      for (const child of node.children) visitNode(child);
    }
  }

  visitNode(root);
  return out;
});

function getRootNodeId(component: Component, source: ElementSourceState) {
  const { nodes } = component.payload;
  if (!source.rootName) return nodes[0]._id;
  const root = nodes.find((n) => getNodeName(n) === source.rootName);
  if (!root)
    throw new Error(
      `Named node not found in ${source.slug}: ${source.rootName}`,
    );
  return root._id;
}

/**
 * Returns true if the interactable or any of its descendants contain text.
 */
export const doesInteractableTreeContainText = (
  interactable: Interactable,
): boolean => {
  if (interactable.text) return true;
  return interactable.children.some(doesInteractableTreeContainText);
};
